import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '../context/SettingsContext';
import client from '../api/client';
import {
    Plus, Calendar as CalendarIcon, Clock, AlertCircle, Trash2, Edit2, 
    CheckCircle2, Circle, X, Receipt, History, FileText, ArrowUpRight, 
    ArrowDownRight, Building, Briefcase, Package, ShoppingCart, 
    Wallet as WalletIcon, Bell, Send, Check, Zap, Target, TrendingUp, Shield,
    Search, ChevronRight, Maximize
} from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import { toast } from 'react-hot-toast';
import InvoiceParticipantField from '../components/InvoiceParticipantField';

const MetricCard = ({ label, value, icon: Icon, color, isMobile }) => (
    <div className="bg-neutral-900 border border-neutral-800 p-8 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem] flex items-center shadow-2xl relative overflow-hidden italic group hover:border-blue-500/10 transition-all">
        <div className={`w-16 h-16 md:w-20 md:h-20 bg-${color}-500/10 text-${color}-500 rounded-2xl md:rounded-[2rem] flex items-center justify-center border border-${color}-500/20 mr-6 md:mr-8 group-hover:scale-110 transition-transform duration-500`}>
            {React.cloneElement(Icon, { size: isMobile ? 24 : 36 })}
        </div>
        <div>
            <p className="text-[9px] md:text-[10px] font-black text-neutral-500 uppercase tracking-widest mb-1 md:mb-2 italic">{label}</p>
            <p className="text-xl md:text-3xl font-black text-white leading-none">{value}</p>
        </div>
    </div>
);

const Invoices = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const { currency } = useSettings();
    
    const [activeTab, setActiveTab] = useState('incoming'); // incoming, outgoing
    const [viewMode, setViewMode] = useState('active'); // active, archive
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [invoiceItems, setInvoiceItems] = useState([]); 
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        client_id: '', project_id: '', product_id: '', amount: '', due_date: new Date().toISOString().split('T')[0],
        type: 'постоянный', notes: '', direction: 'incoming', initial_paid_amount: '', wallet_id: '',
        category: '', frequency: 'monthly', duration: 1,
        guest_data: { name: '', phone: '' }
    });

    const [payData, setPayData] = useState({ amount: '', wallet_id: '', category: 'Продажи', notes: '' });

    // Queries
    const { data: invoices, isLoading } = useQuery({
        queryKey: ['invoices', activeTab],
        queryFn: async () => (await client.get(`/invoices?direction=${activeTab}`)).data
    });
    const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: async () => (await client.get('/crm/leads')).data });
    const { data: wallets } = useQuery({ queryKey: ['wallets'], queryFn: async () => (await client.get('/wallets')).data });
    const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: async () => (await client.get('/projects')).data });
    const { data: products } = useQuery({ queryKey: ['products'], queryFn: async () => (await client.get('/products')).data });
    const { data: events } = useQuery({ queryKey: ['events'], queryFn: async () => (await client.get('/events')).data });

    const createMutation = useMutation({
        mutationFn: (data) => client.post('/invoices', data),
        onSuccess: () => { queryClient.invalidateQueries(['invoices']); setIsCreateModalOpen(false); resetForm(); toast.success('Счет выставлен'); }
    });

    const payMutation = useMutation({
        mutationFn: ({ id, data }) => client.post(`/invoices/${id}/pay`, data),
        onSuccess: () => { queryClient.invalidateQueries(['invoices']); queryClient.invalidateQueries(['wallets']); setIsPayModalOpen(false); toast.success('Оплата принята'); }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => client.delete(`/invoices/${id}`),
        onSuccess: () => { queryClient.invalidateQueries(['invoices']); toast.success('Удалено'); }
    });

    const resetForm = () => {
        setFormData({
            client_id: '', project_id: '', product_id: '', amount: '', due_date: new Date().toISOString().split('T')[0],
            type: 'постоянный', notes: '', direction: activeTab, initial_paid_amount: '', wallet_id: '',
            category: '', frequency: 'monthly', duration: 1,
            guest_data: { name: '', phone: '' }
        });
        setInvoiceItems([]);
        setIsEditing(false);
    };

    const handleOpenPay = (invoice) => {
        setSelectedInvoice(invoice);
        setPayData({ amount: invoice.amount - invoice.paid_amount, wallet_id: '', category: invoice.direction === 'incoming' ? 'Продажи' : 'Прочее', notes: `Оплата по счету ${invoice.number}` });
        setIsPayModalOpen(true);
    };

    const filteredInvoices = invoices?.filter(inv => {
        const matchesSearch = inv.number.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (inv.client_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const isArchived = inv.status === 'Оплачен' || inv.status === 'Отменен';
        return matchesSearch && (viewMode === 'archive' ? isArchived : !isArchived);
    });

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">Счета & Billing</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Million Connections Finance Framework</p>
                </div>
                <button 
                    onClick={() => { resetForm(); setIsCreateModalOpen(true); }} 
                    className="w-full md:w-auto bg-white text-black px-8 md:px-10 py-5 rounded-2xl md:rounded-[2rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-2xl flex items-center justify-center gap-3 italic"
                >
                    <Plus size={20}/> {isMobile ? 'Выставить' : 'Выставить счет'}
                </button>
            </header>

            {/* Metrics Row - Responsive Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <MetricCard 
                    label="Общий оборот" 
                    value={`${(invoices?.reduce((sum, i) => sum + i.amount, 0) || 0).toLocaleString()} ${currency}`} 
                    icon={<Receipt/>} 
                    color="blue" 
                    isMobile={isMobile}
                />
                <MetricCard 
                    label="Факс. получено" 
                    value={`${(invoices?.reduce((sum, i) => sum + i.paid_amount, 0) || 0).toLocaleString()} ${currency}`} 
                    icon={<CheckCircle2/>} 
                    color="emerald" 
                    isMobile={isMobile}
                />
                <MetricCard 
                    label="Дебиторка" 
                    value={`${(invoices?.reduce((sum, i) => sum + (i.amount - i.paid_amount), 0) || 0).toLocaleString()} ${currency}`} 
                    icon={<Clock/>} 
                    color="amber" 
                    isMobile={isMobile}
                />
            </div>

            {/* Main Content Area */}
            <div className="space-y-8">
                {/* Tabs & Search Unified */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl md:rounded-[4rem] p-1.5 flex gap-1 w-full md:w-fit italic shadow-xl">
                        <button onClick={() => setActiveTab('incoming')} className={`flex-1 md:flex-none px-6 md:px-10 py-3.5 md:py-4 rounded-xl md:rounded-[3rem] font-black uppercase text-[9px] md:text-[10px] tracking-[0.2em] transition-all ${activeTab === 'incoming' ? 'bg-blue-600 text-white shadow-xl' : 'text-neutral-500 hover:text-white'}`}>Доходы</button>
                        <button onClick={() => setActiveTab('outgoing')} className={`flex-1 md:flex-none px-6 md:px-10 py-3.5 md:py-4 rounded-xl md:rounded-[3rem] font-black uppercase text-[9px] md:text-[10px] tracking-[0.2em] transition-all ${activeTab === 'outgoing' ? 'bg-rose-600 text-white shadow-xl' : 'text-neutral-500 hover:text-white'}`}>Расходы</button>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                        <input 
                            type="text" 
                            placeholder="Поиск по инвойсу..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            className="w-full bg-neutral-900 border border-neutral-800 rounded-2xl py-4 md:py-4.5 pl-12 text-sm text-white font-bold outline-none focus:border-blue-500 shadow-xl" 
                        />
                    </div>
                </div>

                {/* Content display */}
                <div className="relative min-h-[400px]">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-40 gap-4 opacity-50">
                             <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                             <p className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Проверка документов...</p>
                        </div>
                    ) : filteredInvoices?.length === 0 ? (
                        <div className="py-20 text-center opacity-20 flex flex-col items-center gap-4">
                            <FileText size={48} />
                            <p className="text-xs font-black uppercase tracking-widest">Счета не найдены</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile card list */}
                            <div className="grid grid-cols-1 gap-4 md:hidden">
                                {filteredInvoices?.map(inv => (
                                    <div key={inv.id} className="bg-neutral-900 p-6 rounded-[2.5rem] border border-neutral-800 relative overflow-hidden italic shadow-lg active:scale-[0.98] transition-all">
                                        <div className="flex justify-between mb-4">
                                            <span className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">#{inv.number}</span>
                                            <span className={`px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border ${inv.status === 'Оплачен' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'}`}>{inv.status}</span>
                                        </div>
                                        <div className="space-y-1 mb-6">
                                            <h3 className="text-lg font-black text-white italic uppercase truncate w-full">{inv.client_name || 'Не указан'}</h3>
                                            <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest italic truncate">{inv.project_name || 'Общий счет'}</p>
                                        </div>
                                        <div className="flex justify-between items-end border-t border-white/5 pt-5">
                                            <div>
                                                <p className="text-[8px] font-black text-neutral-600 uppercase mb-1 flex items-center gap-1 italic"><Clock size={10}/> Срок до {new Date(inv.due_date).toLocaleDateString()}</p>
                                                <p className="text-xl font-black text-white italic">{inv.amount.toLocaleString()} {currency}</p>
                                            </div>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleOpenPay(inv); }} 
                                                className="bg-white text-black px-6 py-3.5 rounded-2xl text-[9px] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all italic"
                                            >
                                                Оплатить
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop table view */}
                            <div className="hidden md:block bg-neutral-900 border border-neutral-800 rounded-[4rem] shadow-2xl overflow-hidden italic">
                                <table className="w-full text-left">
                                    <thead className="bg-neutral-950/50">
                                        <tr>
                                            <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest">№ Документа</th>
                                            <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest">Контрагент</th>
                                            <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-center">Статус</th>
                                            <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-right">Баланс</th>
                                            <th className="px-10 py-6 text-[10px] font-black text-neutral-500 uppercase tracking-widest text-right text-transparent">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {filteredInvoices?.map(inv => (
                                            <tr key={inv.id} className="hover:bg-neutral-800/30 transition-all group cursor-pointer" onClick={() => handleOpenPay(inv)}>
                                                <td className="px-10 py-8">
                                                    <p className="text-xl font-black text-white italic tracking-tighter leading-none">#{inv.number}</p>
                                                    <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest italic mt-2">Exp: {new Date(inv.due_date).toLocaleDateString()}</p>
                                                </td>
                                                <td className="px-10 py-8">
                                                    <p className="text-lg font-black text-white italic truncate w-64 leading-none mb-1">{inv.client_name || 'Не указан'}</p>
                                                    <p className="text-[10px] text-neutral-500 font-bold uppercase mt-1 italic tracking-widest">{inv.project_name || 'Общий счет'}</p>
                                                </td>
                                                <td className="px-10 py-8 text-center">
                                                    <span className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest italic border ${
                                                        inv.status === 'Оплачен' 
                                                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                                        : 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                                                    }`}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td className="px-10 py-8 text-right">
                                                    <p className="text-xl font-black text-white italic leading-none mb-1">{inv.amount.toLocaleString()} {currency}</p>
                                                    <p className="text-[10px] text-blue-500 font-bold uppercase tracking-widest italic">Paid: {inv.paid_amount.toLocaleString()}</p>
                                                </td>
                                                <td className="px-10 py-8 text-right">
                                                    <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-all">
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); handleOpenPay(inv); }} 
                                                            className="w-12 h-12 bg-emerald-600/10 text-emerald-500 hover:bg-emerald-600 hover:text-white rounded-xl flex items-center justify-center transition-all shadow-xl"
                                                        >
                                                            <Receipt size={18}/>
                                                        </button>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); if(window.confirm('Удалить счет?')) deleteMutation.mutate(inv.id); }} 
                                                            className="w-12 h-12 bg-rose-500/5 text-rose-500 hover:bg-rose-600 hover:text-white rounded-xl flex items-center justify-center transition-all shadow-xl"
                                                        >
                                                            <Trash2 size={18}/>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Modals & Popups */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[150] flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-5xl rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-2xl flex flex-col italic relative overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="absolute top-0 right-0 p-10 opacity-5 -mr-10 -mt-10 pointer-events-none"><Receipt size={160} /></div>
                        <div className="flex justify-between items-center mb-8 relative z-10">
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter leading-none">Выставить счет</h3>
                                <p className="text-[9px] md:text-[10px] text-blue-600 font-bold uppercase tracking-widest mt-2 italic md:block hidden">Financial Deployment Protocol</p>
                            </div>
                            <button onClick={() => setIsCreateModalOpen(false)} className="w-12 h-12 md:w-14 md:h-14 bg-neutral-800 rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={(e) => { 
                            e.preventDefault(); 
                            const finalItems = products?.filter(p => p.id === parseInt(formData.product_id)).map(p => ({
                                id: p.id,
                                name: p.name,
                                price: p.price,
                                type: 'product'
                            })) || [];

                            if (formData.guest_data?.name) {
                                finalItems.push({
                                    type: 'guest',
                                    name: formData.guest_data.name,
                                    phone: formData.guest_data.phone
                                });
                            }

                            createMutation.mutate({
                                ...formData,
                                items: finalItems
                            }); 
                        }} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 relative z-10 max-h-[75vh] overflow-y-auto pr-4 custom-scrollbar pb-6">
                            <div className="space-y-8">
                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block">Контрагент (Плательщик)</label>
                                    <select required value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 font-bold appearance-none italic transition-all">
                                        <option value="">-- Выбрать клиента --</option>
                                        {clients?.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block">Продукт / Услуга</label>
                                    <select required value={formData.product_id} onChange={(e) => {
                                        const prod = products?.find(p => p.id === parseInt(e.target.value));
                                        setFormData({ ...formData, product_id: e.target.value, amount: prod?.price || formData.amount });
                                    }} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl outline-none focus:border-blue-500 font-bold appearance-none italic transition-all">
                                        <option value="">-- Предмет счета --</option>
                                        {products?.map(p => <option key={p.id} value={p.id}>{p.name} ({p.price?.toLocaleString()} {currency})</option>)}
                                    </select>
                                </div>

                                {events?.some(ev => ev.product_id === parseInt(formData.product_id)) && (
                                    <InvoiceParticipantField 
                                        guestData={formData.guest_data} 
                                        onChange={(val) => setFormData({ ...formData, guest_data: val })}
                                        isMobile={isMobile}
                                    />
                                )}

                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block">Сумма к оплате</label>
                                    <div className="relative">
                                        <input type="number" required value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-6 rounded-2xl outline-none focus:border-blue-500 font-black text-3xl md:text-5xl italic transition-all" placeholder="0.00" />
                                        <span className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-700 font-black italic text-xl">{currency}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block">Крайний срок (Due Date)</label>
                                    <div className="relative">
                                        <CalendarIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                                        <input type="date" required value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 pl-14 rounded-2xl font-bold outline-none h-[68px] focus:border-blue-500 transition-all" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-[9px] font-black text-neutral-500 uppercase tracking-widest px-1 mb-2 block">Комментарий (Internal Notes)</label>
                                    <textarea rows={4} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-6 rounded-2xl outline-none focus:border-blue-500 font-medium italic resize-none" placeholder="Дополнительная информация по счету..." />
                                </div>

                                <div className="pt-4">
                                    <button type="submit" disabled={createMutation.isPending} className="w-full bg-white text-black py-6 md:py-8 rounded-[2rem] md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-[0.2em] shadow-2xl active:scale-95 transition-all italic flex items-center justify-center gap-3">
                                        {createMutation.isPending ? <RefreshCw className="animate-spin" size={18}/> : <Send size={18}/>}
                                        {createMutation.isPending ? 'DEPLOYING...' : 'CONFIRM & DEPLOY'}
                                    </button>
                                    <p className="text-[8px] text-center text-neutral-600 font-black uppercase tracking-[0.3em] mt-6 italic">Protocol: Secure Transaction Layer v3.0</p>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isPayModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[160] flex items-end md:items-center justify-center p-0 md:p-4 animate-in fade-in slide-in-from-bottom-20 duration-500 italic">
                    <div className="bg-neutral-900 border-t md:border border-neutral-800 w-full max-w-md rounded-t-[3rem] md:rounded-[3rem] p-10 shadow-2xl flex flex-col relative overflow-hidden transition-all pb-16 md:pb-10">
                        <div className="flex justify-between mb-8">
                             <h3 className="text-xl font-black text-white italic uppercase tracking-tighter leading-none">Регистрация оплаты</h3>
                             <button onClick={() => setIsPayModalOpen(false)} className="text-neutral-500 hover:text-white transition-all"><X/></button>
                        </div>
                        <div className="p-8 bg-black/40 border border-neutral-800 rounded-[2rem] md:rounded-[2.5rem] mb-10 text-center relative overflow-hidden group">
                            <p className="text-[10px] font-black text-neutral-600 uppercase tracking-widest mb-1 italic">Баланс документа</p>
                            <p className="text-4xl font-black text-white italic tracking-tighter">{(selectedInvoice?.amount - selectedInvoice?.paid_amount).toLocaleString()} {currency}</p>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); payMutation.mutate({ id: selectedInvoice.id, data: payData }); }} className="space-y-6">
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase px-1 tracking-widest">Факт оплаты</label>
                                <input type="number" required autoFocus value={payData.amount} onChange={(e) => setPayData({ ...payData, amount: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-6 rounded-2xl font-black text-4xl text-center outline-none focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase px-1 tracking-widest">Кошелек (Deposit)</label>
                                <select required value={payData.wallet_id} onChange={(e) => setPayData({ ...payData, wallet_id: e.target.value })} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 rounded-2xl font-bold appearance-none outline-none focus:border-blue-500">
                                    <option value="">-- Выбрать счет --</option>
                                    {wallets?.map(w => <option key={w.id} value={w.id}>{w.name} ({w.balance.toLocaleString()})</option>)}
                                </select>
                            </div>
                            <button type="submit" disabled={payMutation.isPending} className="w-full bg-emerald-600 text-white py-6 md:py-7 rounded-[2rem] font-black uppercase text-[10px] md:text-xs tracking-widest shadow-emerald-500/20 shadow-xl active:scale-95 transition-all italic">
                                {payMutation.isPending ? 'Запись данных...' : 'Выполнить транзакцию'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Invoices;
